import { useAutoAnimate } from '@formkit/auto-animate/react';
import { css } from 'aphrodite';
import { useHistory } from 'react-router';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { getFeedItemViewModel, invalidateDashboardFeed, useDashboardFeed } from '../../../queries';
import { ActionItemViewModel } from '../../../viewmodels/AppViewModels';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { Page } from '../../components-v2/PageLayout';
import { AppBarNavigationItemPortalDestinationId } from '../../components/AppBar';
import { KitCard } from '../../components/cards/KitCard';
import { EmptyState } from '../../components/EmptyState';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { Portal } from '../../components/Portal';
import { ContactInCircleGraphic } from '../../components/svgs/graphics/ContactInCircleGraphic';
import { TargetIcon } from '../../components/svgs/icons/TargetIcon';
import { ROUTES } from '../../constants/routes';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { styleSheet } from './styles';

export const KIT_FEED_PAGE_SIZE = 100;
export const KIT_FEED_TYPE = 'Reminder';

export const KeepInTouches = () => {
	const userSession = useUserSession();
	const [listRef] = useAutoAnimate<HTMLDivElement>();
	const history = useHistory();
	const dashboardFeedQuery = useDashboardFeed({
		meta: {
			toastErrorMessage: 'Error loading keep in touch reminders',
			errorLogging: {
				category: 'KeepInTouches',
				action: 'KeepInTouchesPageLoadKeepInTouches-Error',
			},
		},
		pageSize: KIT_FEED_PAGE_SIZE,
		typeOf: KIT_FEED_TYPE,
	});

	const focus = () => {
		history.push(`${ROUTES.contacts.people}/focused?mode=kit&fromPath=/dashboard/keep-in-touches`);
	};

	const onRequestRemoveItem = () => {
		invalidateDashboardFeed({ pageSize: KIT_FEED_PAGE_SIZE, typeOf: KIT_FEED_TYPE });
	};
	const feedItems = dashboardFeedQuery.data?.values ?? [];
	return (
		<Page styles={[styleSheet.container]}>
			<Portal destination={AppBarNavigationItemPortalDestinationId}>
				<NavigationBreadcrumbsBar currentLocationName='Keep In Touches' />
			</Portal>

			{dashboardFeedQuery.status !== 'success' ? (
				<LoadingSpinner type='large' />
			) : !feedItems.length ? (
				<EmptyState
					image={<ContactInCircleGraphic />}
					text={"You don't have any keep in touch reminders"}
					subtext='More reminders will appear on your dashboard as they approach.'
				/>
			) : (
				<div>
					<div className={css(styleSheet.focusButtonContainer)}>
						<button className={css(styleSheet.button, styleSheet.focus)} onClick={focus}>
							<span>
								<TargetIcon />
								<span className={css(styleSheet.buttonText)}>Focused View</span>
							</span>
						</button>
					</div>

					<div ref={listRef}>
						{feedItems.map(x => {
							const itemVM = getFeedItemViewModel({ feedItem: x, userSession });
							if (itemVM instanceof ActionItemViewModel) {
								if (itemVM.isKeepInTouchActionItem) {
									return (
										<KitCard
											actionItem={itemVM}
											className={css(styleSheet.item)}
											key={itemVM.id}
											onRequestRemove={onRequestRemoveItem}
										/>
									);
								}
							}
						})}
					</div>
				</div>
			)}
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
		</Page>
	);
};
